import React, {ReactElement, useRef} from "react";
import {Button, Typography} from "@mui/material";
import {StyledBoxColumn, StyledBoxRow, StyledContainer} from "../../../shared/StyledComponents";
import {DW_SM, PD_LG, PD_MD, PD_SM, PD_XLG, SZ_MD} from "../../../shared/dimens";
import {WordmeshDifficultyLevel, WordmeshGameBoard, WordmeshGameConfig, WordmeshGameData} from "./types";
import {BaseApp, DIALOG_FLAG_SHOW_CLOSE} from "../../../shared/BaseApp";
import {GameFragment, GameFragmentProps, GameFragmentState} from "../GameFragment";
import {HelpOutlineOutlined, MoreHorizOutlined} from "@mui/icons-material";
import {Game} from "../../types";
import {colorDarkBrown, colorEarthBrown, mediumGray, nightGray} from "../../../shared/colors";
import {GAME_WORDMESH} from "../../Games";
import {GameOverResult, GameOverResultType} from "../types";

export type WordmeshGameFragmentProps = GameFragmentProps & {}

enum WordmeshGameStatus {
  STATUS_IN_PROGRESS,
  STATUS_COMPLETE,
}

type WordmeshGameFragmentState = GameFragmentState<WordmeshGameConfig, WordmeshGameData> & {
  gameStatus: WordmeshGameStatus,
  usedLetters: string[],
  message?: string,
}

const BOX_SIZE = 280;

function calcPosition(index: number): number {
  const sq2 = Math.sqrt(2);
  const length = sq2 + 2;
  const positionIndex = index % 3;
  return (1 / sq2 + positionIndex) * BOX_SIZE / length;
}

function drawMesh(ctx: CanvasRenderingContext2D, letters: string[], usedLetters: string[], current?: boolean): void {
  if (!ctx || usedLetters.length < 2) {
    return;
  }
  ctx.beginPath();
  ctx.strokeStyle = `${current ? colorDarkBrown : colorEarthBrown}`;
  ctx.setLineDash([]);
  if (current) {
    ctx.setLineDash([8, 8]);
  }
  ctx.lineWidth = 4;
  for (let i = 0; i < usedLetters.length; i++) {
    const index = letters.indexOf(usedLetters[i]);
    const position = calcPosition(index);
    const sideIndex = Math.floor(index / 3);
    const isTop = sideIndex === 0;
    const isRight = sideIndex === 1;
    const isBottom = sideIndex === 2;
    const isLeft = sideIndex === 3;

    let x: number, y: number;
    // Position letters around the border
    if (isTop) {
      x = position;
      y = 0;
    } else if (isRight) {
      x = BOX_SIZE;
      y = position;
    } else if (isBottom) {
      x = BOX_SIZE - position;
      y = BOX_SIZE;
    } else if (isLeft) {
      x = 0;
      y = BOX_SIZE - position;
    }
    if (i === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.stroke();
}

function WordMesh(props: {
  level: WordmeshDifficultyLevel,
  gameStatus: WordmeshGameStatus,
  foundWords: string[],
  letters: string[],
  usedLetters: string[],
  onLetterClick: (letter: string) => void
}): ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvas = canvasRef.current as HTMLCanvasElement;
  if (canvas) {
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (const word of props.foundWords) {
      drawMesh(ctx, props.letters, word.split(""), false);
    }
    if (props.usedLetters?.length >= 2) {
      drawMesh(ctx, props.letters, props.usedLetters, true);
    }
  }
  return <StyledBoxColumn style={{
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    margin: PD_SM,
  }}>
    <Typography>Try to solve within {props.level.maxTries} words</Typography>
    <Typography style={{height: SZ_MD}}
                variant="h4">{props.gameStatus === WordmeshGameStatus.STATUS_IN_PROGRESS ? props.usedLetters.join("") : undefined}</Typography>
    <div style={{
      position: "relative",
      width: BOX_SIZE,
      height: BOX_SIZE,
      border: "2px solid " + mediumGray,
      borderRadius: PD_SM,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }}>
      <canvas ref={canvasRef} width={BOX_SIZE} height={BOX_SIZE} style={{position: "absolute"}}/>
      {props.letters.map((letter, index) => {

        const usedLetters = props.usedLetters;
        const isUsed = usedLetters.includes(letter);
        const lastLetter = usedLetters.length > 0 && usedLetters[usedLetters.length - 1];
        const lastSideIndex = lastLetter && Math.floor(props.letters.indexOf(lastLetter) / 3);

        const style: React.CSSProperties = {
          position: "absolute",
          width: 40,
          height: 40,
          borderRadius: "50%",
          border: "2px solid " + mediumGray,
          backgroundColor: isUsed ? colorDarkBrown : "white",
          color: isUsed ? "white" : "black",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          fontWeight: "bold",
          fontSize: "18px",
          userSelect: "none"
        };

        const position = calcPosition(index);
        const sideIndex = Math.floor(index / 3);
        const isTop = sideIndex === 0;
        const isRight = sideIndex === 1;
        const isBottom = sideIndex === 2;
        const isLeft = sideIndex === 3;

        // Position letters around the border
        if (isTop) {
          style.top = -20;
          style.left = position - 20;
        } else if (isRight) {
          style.right = -20;
          style.top = position - 20;
        } else if (isBottom) {
          style.bottom = -20;
          style.right = position - 20;
        } else if (isLeft) {
          style.left = -20;
          style.bottom = position - 20;
        }

        return <Typography
          key={`letter-${index}`}
          style={style}
          onClick={() => {
            if (lastSideIndex >= 0 && sideIndex === lastSideIndex) {
              return;
            }
            props.onLetterClick(letter);
          }}
        >
          {letter}
        </Typography>;
      })}
    </div>
  </StyledBoxColumn>;
}

export class WordmeshGameFragment extends GameFragment<WordmeshGameFragmentProps, WordmeshGameConfig, WordmeshGameData, WordmeshGameFragmentState> {

  private board: WordmeshGameBoard;

  protected getGame(): Game {
    return GAME_WORDMESH;
  }

  protected onCreateState(): WordmeshGameFragmentState {
    return {
      ...super.onCreateState(),
      gameStatus: WordmeshGameStatus.STATUS_IN_PROGRESS,
      usedLetters: [],
    };
  }

  componentDidUpdate(prevProps: Readonly<WordmeshGameFragmentProps>, prevState: Readonly<WordmeshGameFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevState.gameData !== this.state.gameData) {
      this.board = this.state.gameData.gamePlay.jsonData as WordmeshGameBoard;
    }
  }

  protected async fetchOnMount(forceReload?: boolean): Promise<void> {
    const gameConfig = this.state.gameConfig;
    if (!gameConfig) {
      return;
    }
    this.setState({
      gameData: await WordmeshGameData.create(gameConfig),
    });
  }

  protected renderConfigContent(): React.ReactElement {
    return this.renderConfigContentForGame(this.getGame(), <>
      {WordmeshDifficultyLevel.VALUES.map(level =>
        <Button key={level.name} variant="contained"
                onClick={() => this.setState({gameConfig: new WordmeshGameConfig(level)})}>
          {level.text}
        </Button>)}
    </>);
  }

  protected renderToolbarTitle(): React.ReactElement {
    if (!this.board) return <Typography>Wordmesh</Typography>;
    return <Typography style={{marginLeft: PD_MD}}>
      {this.board.level.text}
    </Typography>;
  }

  protected renderToolbarButtons() {
    return <>
      <Button onClick={() => BaseApp.CONTEXT.showDialog({flags: DIALOG_FLAG_SHOW_CLOSE}, props => {
        return <StyledBoxColumn style={{padding: PD_LG, maxWidth: DW_SM, alignItems: "center", gap: PD_LG}}>
          <Typography variant="h5">How to play Wordmesh</Typography>
          <Typography>
            Click letters around the box to form words. Each word must be at least 3 letters long.
            Use all the letters in the minimum number of words to complete the puzzle!
          </Typography>
        </StyledBoxColumn>;
      })}>
        <HelpOutlineOutlined/>
      </Button>
      <Button onClick={(event) => BaseApp.CONTEXT.showActions(event.target as HTMLButtonElement, null, [])}>
        <MoreHorizOutlined/>
      </Button>
    </>
  }

  private onLetterClick(letter: string) {
    if (this.state.gameStatus === WordmeshGameStatus.STATUS_COMPLETE) {
      return;
    }

    this.setState({
      usedLetters: [...this.state.usedLetters, letter],
      message: null,
    });
  }

  private onWordSubmit() {
    const word = this.state.usedLetters.join("");

    if (word.length < 3) {
      this.setState({
        message: "Word must be at least 3 letters long",
      });
      return;
    }

    if (!this.board.isValidWord(word)) {
      if (this.board.foundWords.includes(word)) {
        this.setState({
          message: "Word already found",
        });
      } else {
        this.setState({
          message: "Not a valid word",
        });
      }
    } else {
      if (this.board.addWord(word)) {
        this.setState({
          usedLetters: [word.substring(word.length - 1)],
          message: "Great!",
        });
      }

      if (this.board.isComplete()) {
        this.completeGame();
      }
    }

    setTimeout(() => {
      this.setState({message: null});
    }, 2000);
  }

  private completeGame() {
    this.props.gameFragmentListener?.onStatusChanged("Game complete!");
    const duration = this.state.gameData.getDuration();
    const points = this.board.getPoints(duration);
    const resultText = `You completed the puzzle with ${this.board.foundWords.length} words!`;

    this.finishGame(new GameOverResult(
      this.getGame().id,
      GameOverResultType.WON,
      this.board.level.name,
      points,
      duration,
      resultText,
      false
    ));

    this.setState({
      gameStatus: WordmeshGameStatus.STATUS_COMPLETE,
      message: "Complete! 🎉"
    });
  }

  protected renderGameContent(): ReactElement | null {
    if (!this.board) {
      return null;
    }

    const currentWord = this.state.usedLetters.join("");
    return <StyledBoxColumn style={{
      flexGrow: 1,
      width: "100%",
      maxWidth: 600,
      margin: "auto",
      alignItems: "center",
      gap: PD_LG
    }}>
      <StyledBoxRow style={{height: 48, alignItems: "center", justifyContent: "center"}}>
        {this.state.message && (
          <Typography style={{
            paddingLeft: PD_LG,
            paddingRight: PD_LG,
            paddingTop: PD_SM,
            paddingBottom: PD_SM,
            background: nightGray,
            color: "white",
            borderRadius: 4
          }}>
            {this.state.message}
          </Typography>
        )}
      </StyledBoxRow>
      <WordMesh
        level={this.board.level}
        gameStatus={this.state.gameStatus}
        foundWords={this.board.foundWords}
        letters={this.board.layout.letters}
        usedLetters={this.state.usedLetters}
        onLetterClick={this.onLetterClick.bind(this)}
      />
      <StyledContainer style={{gap: PD_LG, alignItems: "center"}}>
        <StyledBoxRow style={{gap: PD_SM, alignItems: "center"}}>
          <Button
            variant="outlined"
            style={{paddingLeft: PD_XLG, paddingRight: PD_XLG}}
            onClick={() => {
              const lastWord = this.board.foundWords[this.board.foundWords.length - 1];
              this.setState({
                usedLetters: lastWord ? [lastWord.substring(lastWord.length - 1)] : [],
              })
            }}
            disabled={currentWord.length === 0}>
            Clear
          </Button>
          <Button
            disabled={!this.state.usedLetters.length}
            variant="outlined"
            style={{paddingLeft: PD_XLG, paddingRight: PD_XLG}}
            onClick={() => {
              const usedLetters = this.state.usedLetters;
              if (usedLetters.length >= 2 || this.board.foundWords.length === 0) {
                this.setState({
                  usedLetters: [...usedLetters.slice(0, usedLetters.length - 1)],
                });
              } else if (this.board.foundWords.length > 0) {
                const lastWord = this.board.foundWords.pop();
                this.setState({
                  usedLetters: lastWord.split(""),
                });
              }
            }}>
            Delete
          </Button>
          <Button
            variant="outlined"
            style={{paddingLeft: PD_XLG, paddingRight: PD_XLG}}
            onClick={() => this.onWordSubmit()}
            disabled={currentWord.length < 3 || this.state.gameStatus === WordmeshGameStatus.STATUS_COMPLETE}>
            Submit
          </Button>
        </StyledBoxRow>
        <StyledBoxRow>
          <Typography style={{flexGrow: 1, fontWeight: "bold"}}>
            Words found: {this.board.foundWords.length}</Typography>
        </StyledBoxRow>
        <Typography>{this.board?.foundWords?.join(", ")}</Typography>
      </StyledContainer>
    </StyledBoxColumn>;
  }
}